import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService, type UpdateUserProfileDto, type CreateInviteDto } from '@/services/users';
import type { UpdateUserData } from '@/types/user';
import { getApiErrorMessage } from '@/utils/apiError';
import { QUERY_KEYS } from './queryKeys';
import { useAuthStore } from './useAuthStore';
import toast from 'react-hot-toast';

// Hook to get current user profile
export const useUserProfile = () => {
  return useQuery({
    queryKey: QUERY_KEYS.users.profile(),
    queryFn: () => usersService.getProfile(),
    staleTime: 300000,
  });
};

// Hook to get all users (Admin)
export const useUsers = () => {
  const scopedOrgId = useAuthStore((s) => s.scopedOrgId);
  return useQuery({
    queryKey: [...QUERY_KEYS.users.lists(), scopedOrgId],
    queryFn: () => usersService.getUsers(),
    staleTime: 30000,
  });
};

// Hook to update a user (Admin).
// Invalidates all caches that embed user data (buildings units, leases, inspections)
// so tenant/landlord names and details stay consistent everywhere.
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserData }) =>
      usersService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.buildings.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leases.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inspections.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenance.all });
      toast.success('User updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update user'));
    },
  });
};

// Hook to delete a user (Admin).
// Same cross-domain invalidation as useUpdateUser.
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.buildings.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leases.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inspections.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.maintenance.all });
      toast.success('User deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete user'));
    },
  });
};

// Hook to get onboarding state
export const useOnboardingState = () => {
  return useQuery({
    queryKey: QUERY_KEYS.users.onboarding(),
    queryFn: () => usersService.getOnboardingState(),
    staleTime: 60000,
  });
};

// Hook to get invite details
export const useInviteDetails = (token: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.users.invite(token),
    queryFn: () => usersService.getInviteDetails(token),
    enabled: !!token,
  });
};

// Hook to update user profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserProfileDto) => usersService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.profile() });
      toast.success('Profile updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    },
  });
};

// Hook to update password
export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      usersService.updatePassword(data),
    onSuccess: () => {
      toast.success('Password updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update password'));
    },
  });
};

// Hook to create invite (Admin only)
export const useCreateInvite = () => {
  return useMutation({
    mutationFn: (data: CreateInviteDto) => usersService.createInvite(data),
    onSuccess: () => {
      toast.success('Invite sent successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to send invite'));
    },
  });
};

// Hook to accept invite
export const useAcceptInvite = () => {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      usersService.acceptInvite(token, { password }),
    onSuccess: () => {
      toast.success('Invite accepted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to accept invite'));
    },
  });
};

// Hook to delete user data (GDPR compliance)
export const useDeleteUserData = () => {
  return useMutation({
    mutationFn: () => usersService.deleteUserData(),
    onSuccess: () => {
      toast.success('User data deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete user data'));
    },
  });
};
